import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  AccountBillingView, TrialGrantBody, getAccountBilling, grantTrial, patchTrial, removeAccountDiscount, revokeTrial,
  sendPortalLink, setAccountCredit,
} from '../../api/platform';
import { updateAccountApi } from '../../api/admin';
import { AccountTrial, atLeastPlatform } from '../../shared/types';
import {
  daysBetween, daysUntil, discountSentence, isoPlusDays, localDay, longDate, moneyExact, shortMD, shortMDY, todayIso,
} from './planDisplay';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { TableCard } from '../../components/fs';
import { usePlatform } from './PlatformLayout';
import { PHONE, useMediaQuery } from '../../shared/media';
import {
  CreditModal, EndNowModal, ExtendModal, GrantModal, MAX_CREDIT_DAYS, RemovePercentModal, TrialRow, endNowOutcome, monDY,
  percentRunning, restorePoint, trialablePlans,
} from './accountBillingModals';
import { Card, PlanPricingRows, Row } from './accountPlanRows';
import { pendingPriceOf } from './ChangePlanDialog';

/** Which money modal is open — one at a time. */
type Open =
  | { kind: 'removePercent' }
  | { kind: 'grant' }
  | { kind: 'extend' }
  | { kind: 'endNow' }
  | { kind: 'credit' };

/** The title a disabled control carries for Staff. */
const ADMIN_ONLY = 'Needs a platform Admin';

/** A subscription Stripe is still billing, or about to. */
const LIVE_SUBSCRIPTION = ['active', 'trialing', 'past_due', 'unpaid'];

/**
 * One account's billing — the account page's Billing tab, in the design's
 * shape (PR 2, handoff screen 04; before it, R12's page at
 * /platform/accounts/:id/billing, which redirects here).
 *
 * The Plan & pricing card is the Overview's, plus what is scheduled, the
 * billing email and the Portal; its "Change plan…" opens the one dialog that
 * sets plan, price and a free period (so do the head's button and the
 * Overview's link). What stayed out of the dialog stays a row action: the
 * percent's Remove, and a running free period's Extend, End at expiry and End
 * now. Credits are a table (in days, D5), the Stripe customer a rail at 1100
 * and up, and the past free periods a ledger. History is the Activity tab.
 *
 * Every action that moves money opens a modal that states what will happen
 * before its button (D3–D5). The billing email stays inline, and the Portal
 * link and "End at expiry" stay one click. Stripe owns every clock; this tab
 * renders the mirror.
 */
export function AccountBillingTab({ accountId, onChanged, onChangePlan, onStartFreePeriod, reloadKey = 0 }: {
  accountId: number;
  /** after any write: the page re-reads its head, tiles and trail */
  onChanged?: () => void;
  /** opens the Change plan dialog — the page owns it */
  onChangePlan?: () => void;
  /** opens it with a free period first switched on */
  onStartFreePeriod?: () => void;
  /** bumped by the page after a write it made (the dialog's Apply): re-read */
  reloadKey?: number;
}) {
  const phone = useMediaQuery(PHONE);
  const { role } = usePlatform();
  const canManage = atLeastPlatform(role, 'admin');
  const toast = useToast();

  const [view, setView] = useState<AccountBillingView | null>(null);
  const [error, setError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [open, setOpen] = useState<Open | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');

  // The credit and the billing email are held here rather than in their
  // modal and row so the unsaved guard can watch them (as it always has).
  const [creditDays, setCreditDays] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  /**
   * The billing email and the credit each have their own Save, so the guard
   * watches both and names neither. The credit modal's Cancel puts its draft
   * back, so only what is open and typed-in counts. The free-period modals are
   * left out: each is a proposal made deliberately, not an edit to what the
   * account already has — and the Change plan dialog is the page's.
   */
  const { dirty, markSaved } = useDirty(
    useMemo(() => ({ emailDraft, creditDays, creditReason }), [emailDraft, creditDays, creditReason]),
    !!view,
  );
  const guard = useUnsavedGuard({ dirty, what: "Your changes to this account's billing" });

  const seedCredit = (v: AccountBillingView) => {
    setCreditDays(v.account.credit_days ? String(v.account.credit_days) : '');
    setCreditReason(v.account.credit_reason || '');
  };

  const load = () => getAccountBilling(accountId)
    .then((r) => {
      setView(r);
      setEmailDraft(r.account.billing_email || '');
      seedCredit(r);
    })
    .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, reloadKey]);

  if (!view) {
    return (
      <div className="pl-ab pl-ao-billing">
        <div className="fs-card st-card-body">{error ? <span className="ad-err">{error}</span> : 'Loading…'}</div>
      </div>
    );
  }

  const { account, plan, stripe } = view;
  const trial = view.trial as TrialRow | null;
  const trialHistory = view.trialHistory as TrialRow[];
  const trialable = trialablePlans(view);
  const pct = percentRunning(account) ? account.discount_percent : null;
  // A credit is pending while Stripe's subscription still carries its `once`
  // coupon; with no live read, the stored days are the best word we have (D5).
  const creditPending = !!account.credit_days && (stripe.discounts ? stripe.discounts.credit : true);
  const deleting = !!account.delete_after;
  const pending = pendingPriceOf(view);
  const back = trial ? restorePoint(view, trial) : null;
  // What End now does at Stripe — the confirm and the toast read this one answer.
  const endNow = trial ? endNowOutcome(view, trial) : null;
  const liveSubscription = !!account.stripe_subscription_id && LIVE_SUBSCRIPTION.includes(account.subscription_status ?? '');
  // The one grant the dialog cannot make: a period that converts, for a
  // customer with no card and no subscription, whose card a Stripe Checkout
  // link collects before it starts (the old grant's "Require a card").
  const cardLink = stripe.connected && !stripe.hasPaymentMethod && !liveSubscription && trialable.length > 0;

  /** Open a modal on the account as it stands. */
  const openModal = (m: Open) => {
    setModalError('');
    if (m.kind === 'credit') seedCredit(view);
    setOpen(m);
  };
  /** Cancel, ✕, Esc or the scrim: what was typed goes with it. */
  const dismiss = () => {
    seedCredit(view);
    setModalError('');
    setOpen(null);
  };
  /** After any write: this tab, and the account page around it (its head, tiles and trail). */
  const reread = async () => { await load(); onChanged?.(); };

  async function onPortalLink() {
    setError('');
    try {
      const r = await sendPortalLink(accountId);
      toast(`Billing Portal link sent${r.emailedTo ? ` to ${r.emailedTo}` : ''}.`);
      onChanged?.();
    } catch (err) { setError((err as Error).message); }
  }

  async function onSaveBillingEmail() {
    setError('');
    setSavingEmail(true);
    try {
      await updateAccountApi(accountId, { billingEmail: emailDraft.trim() });
      toast(emailDraft.trim()
        ? `Billing email saved. Invoices, receipts and Portal links go to ${emailDraft.trim()}.`
        : 'Billing email cleared. Nothing will be emailed to this account until one is set.');
      await reread();
      markSaved();
    } catch (err) { setError((err as Error).message); }
    setSavingEmail(false);
  }

  /**
   * Issue (from its modal) or withdraw (one click, from the row) the one-time
   * credit. The server prices the days, pushes the coupon, emails the
   * customer and reports what it managed — the toast quotes it.
   */
  async function onSaveCredit(clear = false) {
    const fail = clear ? setError : setModalError;
    fail('');
    setBusy(true);
    try {
      const r = await setAccountCredit(accountId, {
        days: clear ? null : Number(creditDays),
        reason: clear ? '' : creditReason.trim(),
      });
      if (!clear) setOpen(null);
      toast([
        clear ? 'Credit withdrawn.' : discountSentence(r.discounts),
        clear ? '' : r.emailed
          ? 'The customer has been emailed.'
          : 'NO EMAIL WENT OUT — outgoing mail is not configured on this server.',
      ].filter(Boolean).join(' '));
      await reread();
      markSaved();
    } catch (err) { fail((err as Error).message); }
    setBusy(false);
  }

  async function onGrant(body: TrialGrantBody) {
    setModalError(''); setCheckoutUrl('');
    setBusy(true);
    try {
      const r = await grantTrial(accountId, body);
      setOpen(null);
      // The server's own word when Stripe could not take the clock (no price).
      const note = (r as { note?: string }).note;
      toast(`Free period granted.${note ? ` ${note}` : ''}`);
      if (r.checkoutUrl) setCheckoutUrl(r.checkoutUrl);
      await reread();
    } catch (err) { setModalError((err as Error).message); }
    setBusy(false);
  }

  async function onExtend(to: string) {
    if (!trial) return;
    setModalError('');
    setBusy(true);
    try {
      const r = await patchTrial(accountId, trial.id, { expiresAt: to });
      setOpen(null);
      toast((to > trial.expires_at.slice(0, 10) ? `Extended to ${longDate(to)}.` : `The free period now ends ${longDate(to)}.`)
        + untouchedAtStripe(r));
      await reread();
    } catch (err) { setModalError((err as Error).message); }
    setBusy(false);
  }

  async function onEndAtExpiry() {
    if (!trial) return;
    setError('');
    try {
      const r = await patchTrial(accountId, trial.id, { autoConvert: false });
      toast(`The period now ends at expiry with no charge.${untouchedAtStripe(r)}`);
      await reread();
    } catch (err) { setError((err as Error).message); }
  }

  async function onEndNow(note: string) {
    if (!trial) return;
    setModalError('');
    setBusy(true);
    try {
      const r = await revokeTrial(accountId, trial.id, note);
      setOpen(null);
      const told = (r as { stripe?: { updated?: boolean } }).stripe?.updated;
      // A restore plan with no Stripe price at their interval cancels the
      // subscription, and the webhook moves the account to Free (the confirm said so).
      toast(endNow === 'cancels' && back?.name && told !== false
        ? 'Ended. Their subscription is cancelled and the account moves to Free.'
        : `Ended. They are back on ${back?.name ?? 'their paid plan'}.`
          + (told === false && stripe.connected && account.stripe_subscription_id ? ' Nothing changed at Stripe.' : ''));
      await reread();
    } catch (err) { setModalError((err as Error).message); }
    setBusy(false);
  }

  async function onRemovePercent() {
    setModalError('');
    setBusy(true);
    try {
      const r = await removeAccountDiscount(accountId);
      setOpen(null);
      // A warning (a scheduled change at renewal Stripe would not re-strike) is worth as much as the success.
      toast([
        r.discounts.applied
          ? 'Percent off removed. Their next invoice is charged without it.'
          : `Percent off removed here. ${r.discounts.reason ?? ''}`,
        r.discounts.warning ?? '',
      ].filter(Boolean).join(' ').trim());
      await reread();
    } catch (err) { setModalError((err as Error).message); }
    setBusy(false);
  }

  // ---- the Plan & pricing card's additions (screen 04) ----
  // Money actions are an Admin's: Staff are shown none of them here, as the
  // billing page never did (the card's own Change plan… is off, as on the Overview).
  const adminButton = (label: string, onClick: () => void, className = 'fs-btn text', aria?: string) => (canManage ? (
    <button type="button" className={className} aria-label={aria} onClick={onClick}>
      {label}
    </button>
  ) : null);

  const planSub = view.price.listCents || pending
    ? `Billed ${(pending && trial?.auto_convert ? pending.interval : view.price.interval) === 'year' ? 'annually' : 'monthly'}`
      + (account.current_period_end && !trial ? ` · this period ends ${monDY(account.current_period_end)}` : '')
    : 'No charge';

  // What is set to change later: the dialog's terms waiting for their phase,
  // or a move at the renewal made elsewhere (the customer's own downgrade).
  // A free period that converts into them says so in its own rows instead.
  const scheduled: ReactNode = pending && !trial?.auto_convert ? (
    <Row label="Scheduled" sub={`From ${monDY(localDay(pending.startsAt))} · Change plan… to alter it`}>
      {pending.summary}
    </Row>
  ) : !pending && stripe.scheduledChange ? (
    <Row label="Scheduled" sub="At their renewal">
      {`${stripe.scheduledChange.planName}`
        + (stripe.scheduledChange.amountCents !== null ? ` · ${moneyExact(stripe.scheduledChange.amountCents)}` : '')}
    </Row>
  ) : null;

  const percentOffBill = pct !== null && stripe.discounts && !stripe.discounts.percent;
  const pricingExtra = (percentOffBill || (pct !== null && account.discount_note) || (account.discount_percent !== null && pct === null)) ? (
    <>
      {pct !== null && account.discount_note && <div className="pl-ao-rowsub">{account.discount_note}</div>}
      {account.discount_percent !== null && pct === null && account.discount_ends_at && (
        <div className="pl-ao-rowsub">An earlier {account.discount_percent}% off ended {monDY(account.discount_ends_at)}.</div>
      )}
      {percentOffBill && (
        <div className="pl-ab-line pl-ab-drift">
          <span className="fs-pill caution" title="Stripe's subscription does not carry it right now">not on the Stripe subscription</span>
        </div>
      )}
    </>
  ) : null;
  const pricingActions = account.discount_percent !== null
    ? adminButton('Remove', () => openModal({ kind: 'removePercent' }), 'fs-btn text danger', 'Remove the percent off')
    : null;

  const nothingToOffer = (
    <div className="pl-ao-rowsub">
      Nothing to offer: no plan above {plan.name} is marked available for free upgrade periods.
    </div>
  );
  const freeActions = trial ? (
    <>
      {adminButton('Extend…', () => openModal({ kind: 'extend' }))}
      {trial.auto_convert && !deleting && adminButton('End at expiry', () => void onEndAtExpiry(), 'fs-btn text', 'End at expiry, no charge')}
      {adminButton('End now', () => openModal({ kind: 'endNow' }), 'fs-btn text danger')}
    </>
  ) : trialable.length > 0 && !deleting ? (
    <>
      {adminButton('Start free period…', () => onStartFreePeriod?.())}
      {cardLink && adminButton('Start with a card link…', () => openModal({ kind: 'grant' }))}
    </>
  ) : null;
  const freeExtra = trial ? (
    <>
      <div className="pl-ab-days">
        <span className="pl-ab-daysn">{daysUntil(trial.expires_at)}</span>
        <span className="st-muted">days left · ends {shortMD(trial.expires_at)} · granted {shortMDY(trial.starts_at)} by {trial.granted_by_name}</span>
      </div>
      <div className="pl-elapsed pl-ab-elapsed"><div style={{ width: `${elapsedPct(trial)}%` }} /></div>
      <div className="pl-ab-facts pl-ab-freefacts">
        <div className="pl-ab-fact">
          <span className={`sso-dot ${stripe.connected && stripe.hasPaymentMethod ? 'pass' : 'warn'}`} />
          {!stripe.connected ? 'Stripe is not connected — conversion cannot run'
            : stripe.hasPaymentMethod ? 'Card on file' : 'No card on file — conversion cannot run'}
        </div>
        <div className="pl-ab-fact">
          <span className={`sso-dot ${trial.notify ? 'pass' : ''}`} />
          {trial.notify
            ? `Grant email sent ${shortMD(trial.starts_at)} · reminder queued ${shortMD(isoPlusDays(trial.expires_at, -7))}`
            : 'No emails for this period'}
        </div>
      </div>
      {trialable.length === 0 && nothingToOffer}
    </>
  ) : trialable.length === 0 ? nothingToOffer : null;

  const contactRows = (
    <>
      {/* The address the emailing actions depend on — "no billing email" is
          the commonest reason any of them refuses. Inline, with its own Save. */}
      <Row
        label={<label htmlFor="ab-email">Billing email</label>}
        sub={account.billing_email
          ? 'Invoices, receipts, Portal links and free-period reminders all go here.'
          : 'Not set — this account cannot be sent a Portal link, a receipt or a reminder until it is.'}
        actions={canManage && (
          <button type="button" className="fs-btn neutral sm" onClick={onSaveBillingEmail}
            disabled={savingEmail || emailDraft.trim() === (account.billing_email || '')}>
            {savingEmail ? 'Saving…' : 'Save'}
          </button>
        )}
      >
        <input id="ab-email" type="email" className="fs-input pl-ab-email" value={emailDraft} disabled={!canManage}
          placeholder="ap@customer.com" onChange={(e) => setEmailDraft(e.target.value)} />
      </Row>
      <Row
        label="Billing Portal"
        sub="Where they add or change a card. Staff never see the link."
        actions={canManage && (
          <button type="button" className="fs-btn neutral sm" onClick={onPortalLink} disabled={!account.billing_email}
            title={account.billing_email
              ? `Emails a Stripe Portal link to ${account.billing_email}`
              : 'Set a billing email first — the link is emailed to the customer, never shown to staff'}>
            Email a Portal link
          </button>
        )}
      >
        {account.billing_email
          ? <>A Stripe Portal link goes to <b>{account.billing_email}</b>.</>
          : <span className="st-muted">Needs a billing email — the link is emailed to the customer, never shown to staff.</span>}
      </Row>
    </>
  );

  // ---- One-time credits (D5: in days, one at a time) ----
  const creditState = creditPending
    ? <span className="fs-pill ok">outstanding</span>
    : <span className="fs-pill neutral">no longer on the bill</span>;
  const creditActions = canManage ? (
    <>
      <button type="button" className="fs-btn text" onClick={() => openModal({ kind: 'credit' })}>Change credit…</button>
      <button type="button" className="fs-btn text danger" disabled={busy} onClick={() => onSaveCredit(true)}>
        Withdraw credit
      </button>
    </>
  ) : null;

  return (
    <div className="pl-ab pl-ao-billing">
      {error && <div className="ad-err" role="alert">{error}</div>}
      {checkoutUrl && (
        <div className="ad-ok pl-ab-banner" role="status">
          The free period needs a card before it converts.{' '}
          <a href={checkoutUrl} target="_blank" rel="noreferrer">Checkout link for their card ↗</a>
        </div>
      )}

      <div className="pl-ab-body">
        <div className="pl-ab-main">
          {/* -------- Plan & pricing (screen 04) -------- */}
          <Card
            title="Plan & pricing"
            className="pl-ao-pricing pl-ab-pricing"
            aside={(
              <button type="button" className="fs-btn text" disabled={!canManage} title={!canManage ? ADMIN_ONLY : undefined}
                onClick={() => onChangePlan?.()}>
                Change plan…
              </button>
            )}
          >
            <PlanPricingRows
              view={view}
              deleteAfter={account.delete_after}
              billing={{
                planSub, afterPlan: scheduled, pricingActions, pricingExtra, freeActions, freeExtra, after: contactRows,
              }}
            />
          </Card>

          {/* -------- One-time credits (screen 04, in days — D5) -------- */}
          <TableCard
            className="pl-ab-credits"
            title="One-time credits"
            hint={`Up to ${MAX_CREDIT_DAYS} days of their own price, off the next invoice, once. It applies alongside either discount.`}
            action={!account.credit_days && canManage && (
              <button type="button" className="fs-btn text" onClick={() => openModal({ kind: 'credit' })}>+ Add credit</button>
            )}
            footer={account.credit_days && !creditPending ? (
              <span className="st-muted">
                Stripe’s subscription no longer carries it — an invoice has used it, or it never reached Stripe.
                Withdraw it so a later plan change does not issue it again.
              </span>
            ) : undefined}
          >
            {!account.credit_days ? (
              <div className="st-card-body st-muted st-empty">No credits yet.</div>
            ) : phone ? (
              <div className="pl-hist-cards pl-ab-hist">
                <div className="pl-hist">
                  <div className="what">{account.credit_days} days · {creditState}</div>
                  {account.credit_reason && <div className="pl-ab-sub">{account.credit_reason}</div>}
                  {account.credit_issued_at && <div className="who">issued {monDY(account.credit_issued_at)}</div>}
                  {creditActions && <div className="pl-ab-credit-acts">{creditActions}</div>}
                </div>
              </div>
            ) : (
              <table className="fs-table">
                <thead>
                  <tr><th>Credit</th><th>Reason</th><th>Issued</th><th><span className="fs-sr-only">Actions</span></th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pl-ab-nowrap"><span className="pl-ab-strong">{account.credit_days} days</span> {creditState}</td>
                    <td>{account.credit_reason || <span className="st-muted">—</span>}</td>
                    <td className="pl-ab-nowrap st-muted">{account.credit_issued_at ? monDY(account.credit_issued_at) : '—'}</td>
                    <td className="fs-row-actions">{creditActions}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </TableCard>
        </div>

        {/* -------- The Stripe customer: a rail at 1100 and up -------- */}
        <aside className="pl-ab-rail" aria-label="Stripe customer">
          <section className="fs-card pl-ab-card">
            <div className="st-card-head">
              <h2>Stripe customer</h2>
              {stripe.connected && stripe.mode && (
                <span className={`fs-pill ${stripe.mode === 'live' ? 'ok' : 'caution'}`}>{stripe.mode} mode</span>
              )}
            </div>
            {/* The billing page's head carried this; on the account page it
                sits with the customer it opens (074). */}
            {stripe.customerUrl && (
              <div className="pl-ao-stripe-open">
                <a className="fs-btn neutral sm" href={stripe.customerUrl} target="_blank" rel="noreferrer">
                  Open customer in Stripe ↗
                </a>
              </div>
            )}
            <div className="st-card-body">
              {!stripe.connected ? (
                <div className="st-muted pl-ab-railnote">
                  Stripe is not connected on this server, so there is no customer to show.
                </div>
              ) : !account.has_stripe_customer ? (
                <div className="st-muted pl-ab-railnote">
                  No Stripe customer yet — one is created on their next billing action.
                </div>
              ) : (
                <>
                  {/* A Customer exists for every account, so it proves nothing
                      about payment. The card row is read from Stripe. */}
                  <RailRow k="Card on file" v={stripe.hasPaymentMethod ? 'yes' : 'no card yet'} />
                  <RailRow k="Subscription" v={account.subscription_status || '—'} />
                  <RailRow k="Trial ends" v={trial ? shortMDY(trial.expires_at) : '—'} />
                  <RailRow k="Next invoice" v={stripe.nextInvoice ? moneyExact(stripe.nextInvoice.amount_cents) : '—'} />
                  {/* "nothing" is the answer worth printing: read live from the
                      schedule, a blank means Stripe holds no pending change. */}
                  <RailRow
                    k="At renewal"
                    v={stripe.scheduledChange
                      ? `${stripe.scheduledChange.planName}`
                        + (stripe.scheduledChange.amountCents !== null
                          ? ` · ${moneyExact(stripe.scheduledChange.amountCents)}` : '')
                      : 'no change scheduled'}
                  />
                  <RailRow k="Lifetime paid" v={moneyExact(stripe.lifetimePaidCents)} />
                  {(account.stripe_customer_id || account.stripe_subscription_id) && (
                    <div className="pl-ab-ids">
                      {account.stripe_customer_id && (
                        <><div className="k">customer</div><div className="v">{account.stripe_customer_id}</div></>
                      )}
                      {account.stripe_subscription_id && (
                        <><div className="k">subscription</div><div className="v">{account.stripe_subscription_id}</div></>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </aside>

        <div className="pl-ab-foot">
          {/* History — every billing action on the account — is the Activity
              tab now, with everything else that happened to it (074). */}

          {/* -------- The free-period ledger -------- */}
          <TableCard className="pl-ab-ledger" title="Past free periods">
            {trialHistory.length === 0 ? (
              <div className="st-card-body st-muted st-empty">No free periods yet.</div>
            ) : phone ? (
              /* Four columns of history, as rows (§4I I3). */
              <div className="pl-hist-cards pl-ab-hist">
                {trialHistory.map((t) => (
                  <div className="pl-hist" key={t.id}>
                    <div className="what">{t.plan_name} · {shortMD(t.starts_at)} → {shortMDY(t.expires_at)}</div>
                    <div className="pl-ab-hist-outcome"><TrialOutcome trial={t} /></div>
                    <div className="who">granted by {t.granted_by_name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <table className="fs-table">
                <thead>
                  <tr><th>Plan</th><th>Period</th><th>Outcome</th><th>Granted by</th></tr>
                </thead>
                <tbody>
                  {trialHistory.map((t) => (
                    <tr key={t.id}>
                      <td className="pl-ab-strong">{t.plan_name}</td>
                      <td className="pl-ab-nowrap">{shortMD(t.starts_at)} → {shortMDY(t.expires_at)}</td>
                      <td><TrialOutcome trial={t} /></td>
                      <td className="st-muted">{t.granted_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>
        </div>
      </div>

      {/* -------- The money modals, one at a time -------- */}
      {open?.kind === 'removePercent' && (
        <RemovePercentModal view={view} busy={busy} error={modalError} onConfirm={onRemovePercent} onClose={dismiss} />
      )}
      {open?.kind === 'grant' && (
        <GrantModal view={view} replacing={trial} busy={busy} error={modalError} onSubmit={onGrant} onClose={dismiss} />
      )}
      {open?.kind === 'extend' && trial && (
        <ExtendModal view={view} trial={trial} busy={busy} error={modalError} onSubmit={onExtend} onClose={dismiss} />
      )}
      {open?.kind === 'endNow' && trial && (
        <EndNowModal view={view} trial={trial} busy={busy} error={modalError} onConfirm={onEndNow} onClose={dismiss} />
      )}
      {open?.kind === 'credit' && (
        <CreditModal
          view={view} creditDays={creditDays} creditReason={creditReason}
          setCreditDays={setCreditDays} setCreditReason={setCreditReason}
          busy={busy} error={modalError} onSubmit={() => onSaveCredit()} onClose={dismiss}
        />
      )}
      {guard.node}
    </div>
  );
}

/** The outcome pill in the ledger, with the resolution note beneath. */
function TrialOutcome({ trial }: { trial: AccountTrial }) {
  // Past its end, it is over — whatever its row says until the sweep or Stripe
  // records how (jobs/freePeriodEnds): ended, or converted.
  if (trial.status === 'active' && new Date(trial.expires_at).getTime() <= Date.now()) {
    return <span className="fs-pill neutral">ended</span>;
  }
  if (trial.status === 'active') {
    return <span className="fs-pill warn">active · {daysUntil(trial.expires_at)}d left</span>;
  }
  if (trial.status === 'converted') {
    return <span className="fs-pill ok">converted to paid</span>;
  }
  if (trial.status === 'revoked') {
    const after = trial.resolved_at ? daysBetween(trial.starts_at, trial.resolved_at) : null;
    return (
      <div>
        <span className="fs-pill neutral">revoked{after !== null ? ` after ${after}d` : ''}</span>
        {trial.resolution_note && <div className="fs-cell-sub">"{trial.resolution_note}"</div>}
      </div>
    );
  }
  return <span className="fs-pill neutral">ended, no charge</span>;
}

/** How much of the period has run — the bar under the days-left number. */
function elapsedPct(trial: AccountTrial): number {
  const total = daysBetween(trial.starts_at, trial.expires_at);
  if (total <= 0) return 100;
  const gone = daysBetween(trial.starts_at, todayIso());
  return Math.min(100, Math.max(0, Math.round((gone / total) * 100)));
}

/**
 * What Extend and End at expiry say when Stripe was left alone: the server
 * writes to Stripe only while it holds the period (a `trialing` subscription),
 * so a period that stood locally over a paying subscription never touches it.
 */
function untouchedAtStripe(r: { stripe?: { updated: boolean } }): string {
  return r.stripe?.updated === false ? ' Nothing changed at Stripe — Stripe is not holding this period.' : '';
}

/** One key–value line of the Stripe customer card. */
function RailRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="pl-ab-kv">
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}
